import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
// components
// @ts-ignore
import { CustomLoader } from "@gl-shared/shared/ui";
import AppHeader from "../../AppHeader";
import AppSidebar from "../../AppSidebar";
// helpers
import {
  delayForHidingFormMsg,
  pagesUrl,
  hotelPanelRootUrl,
  removeTrailingSlash,
  getCookie,
  isTokenExpired,
  startingDateRange
} from "../../../common/utils/panel-helpers/helpers";
// @ts-ignore
import { getFilteredBookings } from "@gl-shared/shared/helpers";
// hooks
import { useDidMountEffect } from "../../../common/hooks/useDidMountEffect";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { homeActionCreators } from "../../../common/store/modules/homeModule";
import { bookingsActionCreators } from "../../../common/store/modules/bookingsModule";
import { settingsActionCreators } from "../../../common/store/modules/settingsModule";
import { globalActionCreators } from "../../../common/store/modules/globalModule";
import { loginsActionCreators } from "../../../common/store/modules/loginsModule";
import { marketplaceActionCreators } from "../../../common/store/modules/marketplaceModule";
import { employeesActionCreators } from "../../../common/store/modules/employeesModule";
import { offersActionCreators } from "../../../common/store/modules/offersModule";

// App Layout for authenticated routes

export default function AppLayoutAuthenticated() {
  const location = useLocation();
  const timerFormMessageRef = useRef(null);
  const navigate = useNavigate();

  // states
  const userToken = useSelector((state: any) => state.login.userToken);
  const isUserAuthenticated = useSelector((state: any) => state.login.isUserAuthenticated);
  const loading = useSelector((state: any) => state.global.loading);
  const selectedHotels = useSelector((state: any) => state.global.selectedHotels);
  const hotelGroup = useSelector((state: any) => state.home.hotelData?.hotelGroup);
  const isHotelGroupExists = useSelector((state: any) => state.home.isHotelGroupExists);
  const allBookings = useSelector((state: any) => state.bookings.allBookings);

  // actions
  const loadHotelInfo = useActions(homeActionCreators?.loadHotelInfoAction, []);
  const loadBookingsAction = useActions(bookingsActionCreators?.loadBookingsAction, []);
  const filterBookingsByHotelAction = useActions(bookingsActionCreators?.filterBookingsByHotelAction, []);
  const loadHotelSettings = useActions(settingsActionCreators?.loadHotelSettingsAction, []);
  const toggleSectionLoader = useActions(globalActionCreators?.toggleSectionLoaderAction, []);
  const setFormData = useActions(globalActionCreators?.setFormDataAction, []);
  const loadLoginsData = useActions(loginsActionCreators?.loadLoginsAction, []);
  const toggleMarketplaceLoader = useActions(marketplaceActionCreators?.toggleMarketplaceLoader, []);
  const loadMarketplaceDataAction = useActions(marketplaceActionCreators?.loadMarketplaceDataAction, []);
  const setLicencesAction = useActions(employeesActionCreators?.setLicencesAction, []);
  const loadEmployeesAction = useActions(employeesActionCreators?.loadEmployeesAction, []);
  const loadOffersAction = useActions(offersActionCreators?.loadOffersAction, []);

  // window pathname with removed "/" at the and
  const locationUrlWithoutSlash = removeTrailingSlash(location?.pathname);

  useEffect(() => {
    if (userToken && !isTokenExpired(getCookie(`${hotelPanelRootUrl}-expires`))) {
      initialCall();
    } else {
      if (isUserAuthenticated === false || isTokenExpired(getCookie(`${hotelPanelRootUrl}-expires`))) {
        navigate(`/${hotelPanelRootUrl}/${pagesUrl.login}`);
      }
    }
    return () => {
      // @ts-ignore
      clearTimeout(timerFormMessageRef.current);
    };
  }, [userToken, isUserAuthenticated]);

  // For Hotel Group Manager: Logic for filtering bookings array when selected hotels has changed
  useDidMountEffect(() => {
    if (isHotelGroupExists) {
      if (selectedHotels[0]?.id === 0) {
        filterBookingsByHotelAction(allBookings);
      } else {
        const filteredBookingResult = getFilteredBookings(allBookings, selectedHotels);
        filterBookingsByHotelAction(filteredBookingResult);
      }
    }
  }, [selectedHotels, allBookings]);

  const hideFormMessage = (delay: any) => {
    // @ts-ignore
    timerFormMessageRef.current = setTimeout(
      () =>
        setFormData({
          formSuccessful: false,
          formError: false
        }),
      delay ? delay : 0
    );
  };

  const handleFormMessage = (status: any, form: any) => {
    if (status === "ok") {
      setFormData({
        formSuccessful: true,
        form: form
      });
    } else if (form === "HotelSelectionNotice") {
      setFormData({
        form: form,
        hotelName: hotelGroup[1]?.name
      });
    } else {
      setFormData({
        formError: true,
        form: form
      });
    }

    // Hiding FormMessage for all cases, EXCEPT for Hotel Group Manager role
    // For that role hiding FormMessage is handled separately --> look handleSectionLoader method
    if (form !== "HotelSelectionNotice") {
      hideFormMessage(delayForHidingFormMsg);
    }
  };

  const handleSectionLoader = (loadingState: any, sectionName: any) => {
    toggleSectionLoader(loadingState, sectionName);

    // Marketplace: edge case for non-hotel-group-manager role and for Marketplace EP
    // here we need separated and additional logic in order to show same loader for Marketplace section, for non-hotel-group-manager role:
    if (isHotelGroupExists === false && sectionName === pagesUrl?.marketplace) {
      toggleMarketplaceLoader(loadingState);
    }

    // Hide FormMessage (with timeout) after loading is finished
    if (loadingState === false) {
      hideFormMessage(delayForHidingFormMsg);
    }
  };

  // Load employee licenses
  const handleEmployeeLicenses = (employees: any) => {
    let mainLicenses = 0;
    let employeeLicenses = 0;

    employees.map((employee: any) => {
      if (employee?.roles?.length > 0) {
        if (
          employee?.roles?.includes("ROLE_BUSINESS_WEB_APP") ||
          employee?.roles?.includes("ROLE_BUSINESS_PANEL") ||
          employee?.roles?.includes("ROLE_BUSINESS_MOBILITY_BOOKER")
        ) {
          mainLicenses++;
        } else if (employee?.roles?.includes("ROLE_BUSINESS_MOBILE_APP")) {
          employeeLicenses++;
        }
      }
    });
    setLicencesAction({
      mainLicenses: mainLicenses,
      employeeLicenses: employeeLicenses
    });
  };

  // Loading hotel settings
  const loadSettings = (hotelId: any) => loadHotelSettings(hotelId, handleSectionLoader);

  // Load Dashboard and Bookings data
  const loadBookings = () => loadBookingsAction(startingDateRange, locationUrlWithoutSlash);

  // Data for Logins component
  const loadLogins = (hotelId: any) => loadLoginsData(hotelId, handleSectionLoader);

  // Data for Employees component
  const loadEmployees = (hotelId: any) => loadEmployeesAction(hotelId, handleSectionLoader, handleEmployeeLicenses);

  // Data for Marketplace component
  const loadMarketplaceData = (hotelId: any) => loadMarketplaceDataAction(hotelId, handleSectionLoader);

  // Data for Offers component
  const loadOffers = (hotelId: any) => loadOffersAction(hotelId, handleSectionLoader);

  const initialCall = () => {
    loadHotelInfo();
    loadBookings();
  };

  return (
    <div className="app-layout" data-cy="hotel-panel">
      <Box className="app-container">
        <div id="page-wrapper">
          {userToken && (
            <>
              <CustomLoader loading={loading} loaderCustomStyle="loader__container__panel" />
              <AppHeader
                loadLogins={loadLogins}
                loadSettings={loadSettings}
                loadMarketplaceData={loadMarketplaceData}
                loadEmployees={loadEmployees}
                handleSectionLoader={handleSectionLoader}
                hideFormMessage={hideFormMessage}
                timerFormMessageRef={timerFormMessageRef}
              />
              <AppSidebar handleFormMessage={handleFormMessage} />
            </>
          )}
          <div className="wrapper wrapper-content">
            <Outlet
              context={{
                loadLogins: loadLogins,
                loadSettings: loadSettings,
                loadEmployees: loadEmployees,
                handleFormMessage: handleFormMessage,
                handleSectionLoader: handleSectionLoader,
                loadOffers: loadOffers,
                loadMarketplaceData: loadMarketplaceData
              }}
            />
          </div>
        </div>
      </Box>
    </div>
  );
}
